import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button } from "@/components/ui/button";
import { TradePositionSideBadge } from "@/components/trade-position-side-badge";
import { cn } from "@/lib/utils";
import { formatTradeDateTimeForDisplay } from "../../../utils/displayDateTime";
import { formatExecutionCandleTypeLabel } from "../../../utils/executionCandleTimeframe";
import { normalizeExecutionCandles } from "../../../utils/executionCandlesNormalize";
import { SL_COLOR, TP_COLOR } from "../../../utils/executionTradeOverlayLevels";
import { formatStrategyRdPrice, strategyRdMatchKey } from "../../../utils/strategyRd";
import { strategyRdConceptChartLabel } from "../../../utils/strategyRdConceptCopy";
import { extractStrategyRdMetaZone, strategyRdIsLevelPairSetupId, strategyRdSlotUsesVolume } from "../../../utils/strategyRdConcepts";
import {
  strategyRdChartChips,
  strategyRdConfluenceChipKey,
  strategyRdSeriesWithChipKeys,
} from "../../../utils/strategyRdChartChips";
import { strategyRdLanesCoveredBySeries } from "../../../utils/strategyRdIndicatorSeries";
import {
  strategyRdConceptDrawingsForMatches,
  strategyRdMergeLevelSegments,
} from "../../../utils/strategyRdConceptDrawings";
import { strategyRdLaneBFromConceptIds, strategyRdLaneBFromRecipeSlots, strategyRdOscParamsFromRecipe, STRATEGY_RD_MA_SPECS } from "../../../utils/strategyRdConceptIndicators";
import { strategyRdOpposingSrDrawings, parseStrategyRdMatchSr, strategyRdSrLegendItems } from "../../../utils/strategyRdConceptSr";
import { strategyRdConceptFrameBarTimes } from "../../../utils/strategyRdConceptViewport";
import {
  strategyRdCandlesHavePositiveVolume,
  strategyRdDetectChartHighlights,
  strategyRdMatchChartSelectHits,
  type StrategyRdDetectMode,
} from "../../../utils/strategyRdDetect";
import {
  strategyRdIncompleteEntryMessage,
  strategyRdMatchDisplayDirection,
  strategyRdPathAppliedLevels,
  strategyRdPathHighlightBarTime,
  strategyRdPathReadyEnter,
  strategyRdShouldShowIncompleteEntryBanner,
  strategyRdPathLevelsCaption,
  strategyRdPathSetupZone,
} from "../../../utils/strategyRdPath";
import { patternBarPalette } from "../../../utils/vela/tesseraPatternBarHighlight";
import type { ExecutionCandle, ExecutionTradeOverlay } from "../../../types/trade";
import type {
  StrategyRdAppliedLevels,
  StrategyRdEntryGuidance,
  StrategyRdMatch,
  StrategyRdMatchMarker,
  StrategyRdOhlcBar,
  StrategyRdPreviewEmptyState,
  StrategyRdPreviewEmptyStateAction,
  StrategyRdSessionOption,
  StrategyRdSrRail,
} from "../../../types/strategyRd";
import ExecutionCandlesChart from "../TradeLog/components/TradeReview/ExecutionCandlesChart";
import type { StrategyRdChartSymbolProvider } from "../TradeLog/components/TradeReview/ExecutionCandlesVelaChart";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { TradeReviewChartLegendArrow } from "../TradeLog/components/TradeReview/TradeReviewChartLegendArrow";
import {
  strategyRdMetaClassName,
  strategyRdSectionTitleClassName,
  strategyRdBrandActionButtonClassName,
  STRATEGY_RD_CHART_HEIGHT,
} from "./strategyRdCardShell";

const ENTRY_LINE_COLOR = "#7DD3E8";
const RISK_FILL = "rgba(224, 1, 1, 0.35)";
const REWARD_FILL = "rgba(6, 227, 99, 0.35)";
const ZONE_FILL = "rgba(125, 211, 232, 0.28)";
const ZONE_LINE = "#7DD3E8";
const CANDLE_UP = "#8E97A6";
const CANDLE_DOWN = "#374151";

export type StrategyRdDetectChartProps = {
  mode: StrategyRdDetectMode;
  matches: StrategyRdMatch[];
  matchMarkers?: readonly StrategyRdMatchMarker[] | null;
  selectedMatch?: StrategyRdMatch | null;
  /** Top-level preview/scan entry config guidance (prefer over path.user_message). */
  entryGuidance?: StrategyRdEntryGuidance | null;
  candles: StrategyRdOhlcBar[];
  candleType: string;
  symbolLabel?: string | null;
  identityReady?: boolean;
  previewCommitted?: boolean;
  loading?: boolean;
  loadingLabel?: string | null;
  timeZone?: string;
  sessionLabelFor?: (match: StrategyRdMatch) => string | null;
  setupId?: string | null;
  /** Confluence ids and optional per-slot TFs (cross-TF geometry is projected onto the chart). */
  confluences?: readonly (string | { id: string; candle_type?: string | null })[] | null;
  /** Lane-B MA ids — typically same-TF only so the continuous line matches the series. */
  laneBConceptIds?: readonly string[] | null;
  /** Setup + confluence params (fast/slow/length) for drawing the actual MAs in use. */
  laneBRecipe?: {
    setup?: { id?: string | null; params?: Record<string, unknown> | null } | null;
    confluences?: readonly {
      id?: string | null;
      params?: Record<string, unknown> | null;
    }[] | null;
  } | null;
  /** Preset sessions for killzone shade. */
  sessions?: readonly StrategyRdSessionOption[] | null;
  srScopes?: readonly string[] | null;
  srRails?: readonly StrategyRdSrRail[] | null;
  diagnostics?: readonly string[] | null;
  /**
   * BE indicator-confluence series (`ohlc_stats.confluences.indicator_series`, keyed by slot
   * key): MAs / bands over price, MACD / ATR / ADR / correlation / Mansfield in sub-panels.
   */
  indicatorSeries?: Record<string, unknown> | null;
  /** Catalog name for a concept id — chart legend group names ("Confluence 1 · TTM squeeze"). */
  conceptLabel?: ((id: string) => string | null | undefined) | null;
  levels?: StrategyRdAppliedLevels | null;
  /** After Scan — live Entry/SL/TP for every match (Trade Review position tools). */
  ruleTrades?: readonly ExecutionTradeOverlay[] | null;
  selectedTradeId?: string | null;
  onSelectTrade?: (tradeId: string | null) => void;
  /**
   * Lab workspace: fill the parent's height (chart measured to fit), one caption line,
   * no card chrome / legend / footnote.
   */
  fill?: boolean;
  /** Shown in the caption ("Times in Europe/Athens"). */
  timeZoneLabel?: string | null;
  /** Extra controls on the caption row (e.g. the matches toggle). */
  captionActions?: React.ReactNode;
  /** Fill mode: the chart's bottom bar — left (window picker) and right (time zone). */
  footerStart?: React.ReactNode;
  footerEnd?: React.ReactNode;
  /** Lab: the key strip's visibility, owned by the page (Chart settings). Uncontrolled when omitted. */
  legendHidden?: boolean;
  onLegendHiddenChange?: (hidden: boolean) => void;
  /** Lab: candle palette from Chart settings (defaults to green / red). */
  candleScheme?: StrategyRdCandleSchemeId;
  /**
   * Lab: pick the market from the chart's own top bar — symbol search over these instruments,
   * timeframe chips over `allowedCandleTypes`.
   */
  symbolProvider?: StrategyRdChartSymbolProvider | null;
  allowedCandleTypes?: readonly string[];
  onCandleTypeChange?: (candleType: string) => void;
  onInstrumentChange?: (symbol: string) => void;
  /** "Ask TES about this" on a candle / trade marker. */
  onAskAbout?: (target: { timeMs: number; tradeId: string | null }) => void;
  /** False when the page shows the "no entry" reason itself. */
  showPathNotice?: boolean;
  sessionLabel?: string | null;
  detectCountLabel?: string | null;
  onPreview?: () => void;
  previewDisabled?: boolean;
  /** Window overview key — Preview / Scan load frames the full candle range. */
  frameGuideKey?: string | null;
  /**
   * Rail-driven zoom token. When set, chart zooms onto the selected match path.
   * Chart clicks must NOT bump this — they keep the current viewport.
   */
  focusMatchToken?: string | null;
  /** Matches column is hidden — offer a way to bring it back beside the chart. */
  matchesRailHidden?: boolean;
  onShowMatches?: () => void;
  /** Zero-hit Preview copy. Shown with the path note in one box above the chart. */
  previewEmpty?: StrategyRdPreviewEmptyState | null;
  onPreviewEmptyAction?: (action: StrategyRdPreviewEmptyStateAction) => void;
};

function directionLabel(direction: StrategyRdMatch["direction"]): string {
  if (direction === "long") return "Long";
  if (direction === "short") return "Short";
  return "Neutral";
}

function LegendZone({ color }: { color: string }): JSX.Element {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 shrink-0 rounded-[2px]"
      style={{ backgroundColor: color }}
    />
  );
}

function LegendSolidLine({ color }: { color: string }): JSX.Element {
  return (
    <span
      aria-hidden
      className="inline-block h-0.5 w-4 shrink-0 rounded-[1px]"
      style={{ backgroundColor: color }}
    />
  );
}

function LegendBarWash({ direction }: { direction: StrategyRdMatch["direction"] }): JSX.Element {
  const palette = patternBarPalette(
    direction === "short" ? "short" : direction === "long" ? "long" : "neutral"
  );
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-2.5 shrink-0 rounded-[1px]"
      style={{
        backgroundColor: palette.fill,
        boxShadow: `inset 0 0 0 1px ${palette.solid}`,
      }}
    />
  );
}

function LegendPathArrow({
  direction,
}: {
  direction: StrategyRdMatch["direction"];
}): JSX.Element {
  if (direction === "short" || direction === "long") {
    return <TradeReviewChartLegendArrow direction={direction} className="shrink-0" />;
  }
  return (
    <span
      aria-hidden
      className="inline-block size-2.5 shrink-0 rotate-45 rounded-[1px]"
      style={{ backgroundColor: patternBarPalette("neutral").solid }}
    />
  );
}

function StrategyRdChartLegend({
  direction,
  showLevels,
  showPath,
  pathKinds,
  conceptLabels,
  zoneLabel,
  maLines,
  slim = false,
  trailing = null,
}: {
  direction: StrategyRdMatch["direction"];
  /** Lab: one tight row under the chart. */
  slim?: boolean;
  /** Right-aligned control (e.g. Hide legend). */
  trailing?: React.ReactNode;
  showLevels: boolean;
  showPath: boolean;
  pathKinds?: ReadonlySet<
    | "break"
    | "rejection"
    | "retest"
    | "location_armed"
    | "respect"
    | "reject"
    | "break_retest"
    | "entry"
    | "setup"
    | "invalid"
    | "marker"
  >;
  conceptLabels?: readonly string[];
  zoneLabel: string | null;
  maLines: readonly { title: string; color: string }[];
}): JSX.Element {
  const showBreak = Boolean(pathKinds?.has("break"));
  const showRejection = Boolean(pathKinds?.has("rejection"));
  const showRetest = Boolean(pathKinds?.has("retest"));
  const showLocationArmed = Boolean(pathKinds?.has("location_armed"));
  const showRespect = Boolean(pathKinds?.has("respect"));
  const showRejectIx = Boolean(pathKinds?.has("reject"));
  const showBreakRetest = Boolean(pathKinds?.has("break_retest"));
  const showEnter = Boolean(pathKinds?.has("entry"));
  const showSetupWash =
    Boolean(pathKinds?.has("setup")) ||
    Boolean(pathKinds?.has("marker")) ||
    showBreak ||
    showRejection ||
    showRetest ||
    showLocationArmed ||
    showRespect ||
    showRejectIx ||
    showBreakRetest ||
    showEnter;
  const setupTags = (conceptLabels ?? []).filter(Boolean);

  return (
    <div
      className={cn(
        "flex w-full items-center",
        slim
          ? "min-h-[36px] gap-3 !border-0 !border-solid !border-border !border-t px-3 py-1.5"
          : "mt-2 min-h-[42px] flex-col justify-center rounded-md border border-border px-3 py-2.5"
      )}
      data-strategy-rd-chart-legend=""
      aria-label="Strategy Lab chart legend"
    >
      {slim ? (
        <span className="inline-flex h-5 shrink-0 items-center self-start font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-[color:var(--brand-tropical-surf)]">
          Key
        </span>
      ) : null}
      <div
        className={cn(
          "flex w-full min-w-0 flex-wrap items-center gap-y-2",
          slim ? "flex-1 justify-start gap-x-5" : "justify-center gap-x-6"
        )}
      >
        {showPath ? (
          <>
            {zoneLabel ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendZone color={ZONE_FILL} />
                <LegendSolidLine color={ZONE_LINE} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  {zoneLabel}
                </span>
              </span>
            ) : null}
            {setupTags.map((label) => (
              <span key={`concept:${label}`} className="inline-flex h-5 items-center gap-1.5">
                <span
                  aria-hidden
                  className="inline-flex h-4 items-center rounded-[3px] px-1 font-[family-name:var(--font-primary)] text-[10px] font-semibold leading-none text-white"
                  style={{ backgroundColor: patternBarPalette("neutral").solid }}
                >
                  {label}
                </span>
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Setup tag
                </span>
              </span>
            ))}
            {showSetupWash ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendBarWash direction={direction} />
                <span
                  aria-hidden
                  className="inline-block h-3 w-1.5 shrink-0 rounded-[1px]"
                  style={{ backgroundColor: CANDLE_UP }}
                />
                <span
                  aria-hidden
                  className="inline-block h-3 w-1.5 shrink-0 rounded-[1px]"
                  style={{ backgroundColor: CANDLE_DOWN }}
                />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Path bar
                </span>
              </span>
            ) : null}
            {showBreak ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Break
                </span>
              </span>
            ) : null}
            {showRejection ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Reject
                </span>
              </span>
            ) : null}
            {showLocationArmed ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Loc check
                </span>
              </span>
            ) : null}
            {showRespect ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Respect
                </span>
              </span>
            ) : null}
            {showRejectIx ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Reject ix
                </span>
              </span>
            ) : null}
            {showBreakRetest ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Break retest
                </span>
              </span>
            ) : null}
            {showRetest ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Retest
                </span>
              </span>
            ) : null}
            {showEnter ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendPathArrow direction={direction} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  Enter
                </span>
              </span>
            ) : null}
          </>
        ) : maLines.length === 0 ? (
          <>
            <span className="inline-flex h-5 items-center gap-1.5">
              <LegendBarWash direction={direction} />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                Pattern highlight
              </span>
            </span>
            {zoneLabel ? (
              <span className="inline-flex h-5 items-center gap-1.5">
                <LegendZone color={ZONE_FILL} />
                <LegendSolidLine color={ZONE_LINE} />
                <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                  {zoneLabel}
                </span>
              </span>
            ) : null}
          </>
        ) : null}
        {showLevels ? (
          <>
            <span
              className="hidden h-3 w-px shrink-0 bg-gray-200 sm:block dark:bg-sidebar-border"
              aria-hidden
            />
            <span className="inline-flex h-5 items-center gap-1.5">
              <LegendSolidLine color={ENTRY_LINE_COLOR} />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                Entry
              </span>
            </span>
            <span className="inline-flex h-5 items-center gap-1.5">
              <LegendZone color={RISK_FILL} />
              <LegendSolidLine color={SL_COLOR} />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                Stop
              </span>
            </span>
            <span className="inline-flex h-5 items-center gap-1.5">
              <LegendZone color={REWARD_FILL} />
              <LegendSolidLine color={TP_COLOR} />
              <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
                Target
              </span>
            </span>
          </>
        ) : null}
        {maLines.map((line) => (
          <span key={line.title} className="inline-flex h-5 items-center gap-1.5">
            <LegendSolidLine color={line.color} />
            <span className="font-[family-name:var(--font-primary)] text-xs font-medium leading-5 text-gray-500 dark:text-sidebar-muted-foreground">
              {line.title}
            </span>
          </span>
        ))}
      </div>
      {trailing}
    </div>
  );
}

const LEGEND_HIDDEN_KEY = "strategyRd.lab.legendHidden";

export function readLegendHidden(): boolean {
  try {
    return window.localStorage.getItem(LEGEND_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeLegendHidden(hidden: boolean): void {
  try {
    window.localStorage.setItem(LEGEND_HIDDEN_KEY, hidden ? "1" : "0");
  } catch {
    // Storage blocked — the choice lasts until reload.
  }
}

/** Lab candle palettes (Chart settings → Candles). */
export type StrategyRdCandleScheme = {
  id: string;
  label: string;
  up: string;
  down: string;
  wick?: string;
  borderUp?: string;
  borderDown?: string;
};

export const STRATEGY_RD_CANDLE_SCHEMES = [
  { id: "greenRed", label: "Tessera", up: PNL_POSITIVE_COLOR, down: PNL_NEGATIVE_COLOR },
  { id: "neon", label: "Clean neon", up: "#2EDBAD", down: "#EA5E85", wick: "#C6C7C1" },
  { id: "pastel", label: "Soft pastel", up: "#8EEED3", down: "#DDA8BC", wick: "#B8B9BA" },
  { id: "cyber", label: "Purple cyber", up: "#B7A4F3", down: "#EB7ABC", wick: "#DF9BCA" },
  // White bodies need an outline on the light chart.
  { id: "mono", label: "Black & white", up: "#FFFFFF", down: "#808082", wick: "#9CA3AF", borderUp: "#6B7280" },
  { id: "ocean", label: "Ocean blue", up: "#5ABAEA", down: "#195DD8", wick: "#6BBEF0" },
  { id: "sunset", label: "Warm sunset", up: "#F8C961", down: "#EB7689", wick: "#E8B08C" },
  { id: "gold", label: "Luxury gold", up: "#DDBD82", down: "#886835", wick: "#D9B66E" },
] as const satisfies ReadonlyArray<StrategyRdCandleScheme>;

export type StrategyRdCandleSchemeId = (typeof STRATEGY_RD_CANDLE_SCHEMES)[number]["id"];

const CANDLE_SCHEME_KEY = "strategyRd.lab.candleScheme";

export function readCandleScheme(): StrategyRdCandleSchemeId {
  try {
    const saved = window.localStorage.getItem(CANDLE_SCHEME_KEY);
    const hit = STRATEGY_RD_CANDLE_SCHEMES.find((s) => s.id === saved);
    if (hit) return hit.id;
  } catch {
    // Storage blocked — default palette.
  }
  return "greenRed";
}

export function writeCandleScheme(id: StrategyRdCandleSchemeId): void {
  try {
    window.localStorage.setItem(CANDLE_SCHEME_KEY, id);
  } catch {
    // Storage blocked — the choice lasts until reload.
  }
}

/** Shared Preview / Scan chart: full-window `candles` + `match_markers` + drawings. */
export function StrategyRdDetectChart({
  mode,
  matches,
  matchMarkers = null,
  selectedMatch = null,
  entryGuidance = null,
  candles,
  candleType,
  symbolLabel = null,
  identityReady = false,
  previewCommitted = false,
  loading = false,
  loadingLabel = null,
  timeZone,
  sessionLabelFor,
  setupId = null,
  confluences = null,
  laneBConceptIds = null,
  laneBRecipe = null,
  sessions = null,
  srScopes = null,
  srRails = null,
  diagnostics = null,
  indicatorSeries = null,
  conceptLabel = null,
  levels = null,
  ruleTrades = null,
  selectedTradeId = null,
  onSelectTrade,
  onAskAbout,
  showPathNotice = true,
  fill = false,
  timeZoneLabel = null,
  captionActions = null,
  footerStart = null,
  footerEnd = null,
  legendHidden: legendHiddenProp,
  onLegendHiddenChange,
  candleScheme = "greenRed",
  symbolProvider = null,
  allowedCandleTypes,
  onCandleTypeChange,
  onInstrumentChange,
  sessionLabel = null,
  detectCountLabel = null,
  onPreview,
  previewDisabled = false,
  frameGuideKey = null,
  focusMatchToken = null,
  matchesRailHidden = false,
  onShowMatches,
  previewEmpty = null,
  onPreviewEmptyAction,
}: StrategyRdDetectChartProps): JSX.Element {
  const match = selectedMatch;
  const resolvedSessionLabel =
    sessionLabel || (match && sessionLabelFor ? sessionLabelFor(match) : null);
  const normalized = useMemo(
    () =>
      normalizeExecutionCandles(
        candles.map(
          (c): ExecutionCandle => ({
            time: c.timestamp,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
            volume: c.volume ?? null,
            candleType: candleType || null,
            symbol: symbolLabel || null,
          })
        )
      ),
    [candleType, candles, symbolLabel]
  );

  const displayDirection = useMemo(
    () => strategyRdMatchDisplayDirection(match),
    [match]
  );
  const conceptDrawings = useMemo(() => {
    // Geometry for the focused match only — other hits stay as quiet column washes.
    const focusMatches = match ? [match] : matches.slice(0, 1);
    const fromMatches = strategyRdConceptDrawingsForMatches({
      matches: focusMatches,
      setupId,
      confluences,
      candles,
      chartCandleType: candleType,
      setupCandleType: candleType,
      sessions,
    });
    const sr = strategyRdOpposingSrDrawings({
      rails: srRails,
      matchMeta: match?.meta,
      setupBarTime:
        match?.path?.setup_bar_time?.trim() || match?.bar_time?.trim() || null,
      entryBarTime: strategyRdPathReadyEnter(match?.path)?.barTime ?? null,
    });
    // S/R rails belong to the Clear-of-opposing-S/R chip's legend group.
    const srChip = (confluences ?? []).findIndex(
      (c) => (typeof c === "string" ? c : c.id) === "clear_of_opposing_sr"
    );
    const srGroup = srChip >= 0 ? strategyRdConfluenceChipKey(srChip) : "setup";
    // Same level from the confluence and the S/R rail → one continuous line.
    return strategyRdMergeLevelSegments([
      ...fromMatches,
      ...sr.map((d) => ({ ...d, group: d.group ?? srGroup })),
    ]);
  }, [candleType, candles, confluences, match, matches, sessions, setupId, srRails]);
  const mountVolumeHistogram = useMemo(() => {
    if (!strategyRdCandlesHavePositiveVolume(candles)) return false;
    // The BE volume panel (volume confluences) replaces the chart's own volume pane.
    const beVolume = Object.values(indicatorSeries ?? {}).some((raw) => {
      const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
      return row?.pane === "volume" || row?.concept_id === "up_down_vol_ratio";
    });
    if (beVolume) return false;
    const slots: Array<{ id?: string | null; params?: Record<string, unknown> | null }> = [];
    if (laneBRecipe?.setup?.id || (laneBRecipe?.confluences?.length ?? 0) > 0) {
      if (laneBRecipe?.setup) slots.push(laneBRecipe.setup);
      for (const slot of laneBRecipe?.confluences ?? []) slots.push(slot);
    } else {
      if (setupId) slots.push({ id: setupId });
      for (const item of confluences ?? []) {
        slots.push(typeof item === "string" ? { id: item } : { id: item.id });
      }
    }
    if (slots.some((slot) => strategyRdSlotUsesVolume(slot))) return true;
    return conceptDrawings.some((drawing) => drawing.paneId === "volume");
  }, [candles, conceptDrawings, confluences, indicatorSeries, laneBRecipe, setupId]);
  const pathHighlights = useMemo(() => {
    const raw = strategyRdDetectChartHighlights({
      mode,
      matches,
      markers: matchMarkers ?? [],
      selectedMatch: match,
    });
    // Setup identity is the concept zone pill (OB / BB / …). Drop the duplicate
    // arrow+label from match_markers on the same setup bar. The Enter pill and
    // entry price line own the fill, so path Enter arrows stay off.
    const hasSetupZone = conceptDrawings.some((d) => d.id.startsWith("setup-zone:"));
    const hasConceptEnter = conceptDrawings.some(
      (d) => d.id.includes(":enter") && d.showLabel !== false
    );
    if (!match) return raw;
    const setupTime =
      match.path?.setup_bar_time?.trim() || match.bar_time?.trim() || "";
    return raw.map((h) => {
      const bar = h.barTime.trim();
      if (hasConceptEnter && h.kind === "entry") {
        return {
          ...h,
          showText: false,
          showArrow: false,
        };
      }
      if (!hasSetupZone || !setupTime) return h;
      if (h.kind !== "marker") return h;
      if (bar !== setupTime) return h;
      if (!h.showArrow && !h.showText) return h;
      // Keep the column wash; drop only the duplicate setup triangle + text.
      return {
        ...h,
        label: "",
        showText: false,
        showArrow: false,
        showWash: true,
        emphasis: h.emphasis === "strong" ? "normal" : h.emphasis ?? "muted",
      };
    });
  }, [conceptDrawings, match, matchMarkers, matches, mode]);
  const enabledLaneBOverlays = useMemo(() => {
    if (laneBRecipe?.setup?.id || (laneBRecipe?.confluences?.length ?? 0) > 0) {
      const fromRecipe = strategyRdLaneBFromRecipeSlots({
        setup: laneBRecipe?.setup ?? { id: setupId },
        confluences:
          laneBRecipe?.confluences ??
          (confluences ?? []).map((c) =>
            typeof c === "string" ? { id: c } : { id: c.id }
          ),
      });
      // Keep oscillator / BB lanes from bare concept ids (rsi, macd, …).
      const oscFromIds = strategyRdLaneBFromConceptIds([
        setupId ?? "",
        ...(laneBConceptIds ??
          (confluences ?? []).map((c) => (typeof c === "string" ? c : c.id))),
      ]).filter((id) => !id.startsWith("sma") && !id.startsWith("ema"));
      return Array.from(new Set([...fromRecipe, ...oscFromIds]));
    }
    return strategyRdLaneBFromConceptIds([
      setupId ?? "",
      ...(laneBConceptIds ??
        (confluences ?? []).map((c) => (typeof c === "string" ? c : c.id))),
    ]);
  }, [confluences, laneBConceptIds, laneBRecipe, setupId]);
  /** Composer chip ids in order (chart legend group `conf-N` = chip N). */
  const confluenceChipIds = useMemo(
    () =>
      (laneBRecipe?.confluences ??
        (confluences ?? []).map((c) => (typeof c === "string" ? { id: c } : { id: c.id }))
      ).map((c) => (c.id || "").trim()),
    [confluences, laneBRecipe]
  );
  const chipTaggedSeries = useMemo(
    () => strategyRdSeriesWithChipKeys(indicatorSeries, confluenceChipIds),
    [confluenceChipIds, indicatorSeries]
  );
  /** One legend group per recipe chip: Setup · …, Confluence N · …, Volume, Entry · …. */
  const chartChips = useMemo(() => {
    const covered = strategyRdLanesCoveredBySeries(indicatorSeries);
    const hasVolumePanel = Object.values(indicatorSeries ?? {}).some((raw) => {
      const row = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
      return row?.pane === "volume" || row?.concept_id === "up_down_vol_ratio";
    });
    return strategyRdChartChips({
      setupId,
      setupSlot: laneBRecipe?.setup ?? null,
      confluences:
        laneBRecipe?.confluences ??
        (confluences ?? []).map((c) => (typeof c === "string" ? { id: c } : { id: c.id })),
      entryMode: match?.path?.entry_mode ?? matches[0]?.path?.entry_mode ?? null,
      enabledLanes: enabledLaneBOverlays.filter((lane) => !covered.has(lane)),
      hasVolumePanel,
      labelFor: conceptLabel ?? undefined,
    });
  }, [conceptLabel, confluences, enabledLaneBOverlays, indicatorSeries, laneBRecipe, match, matches, setupId]);
  const laneBOscParams = useMemo(
    () =>
      strategyRdOscParamsFromRecipe({
        setup: laneBRecipe?.setup ?? (setupId ? { id: setupId } : null),
        confluences:
          laneBRecipe?.confluences ??
          (confluences ?? []).map((c) =>
            typeof c === "string" ? { id: c } : { id: c.id, params: (c as { params?: Record<string, unknown> }).params }
          ),
        matchMeta: (match?.meta as Record<string, unknown> | null | undefined) ?? null,
      }),
    [confluences, laneBRecipe, match?.meta, setupId]
  );
  /** When the selected setup became a closed trade: its exit, so the view runs setup → exit. */
  const selectedTradeExit = useMemo(() => {
    if (!match || !ruleTrades?.length) return null;
    const key = strategyRdMatchKey(match);
    const trade = ruleTrades.find((t) => t.trade_id === key);
    return trade?.exit_time && trade.exit_price != null ? String(trade.exit_time) : null;
  }, [match, ruleTrades]);
  const frameAroundBarTimes = useMemo(() => {
    // Zoom focus = formation geometry (when BE sends it) + path timeline.
    const times = strategyRdConceptFrameBarTimes(match);
    const setup =
      match?.path?.setup_bar_time?.trim() ||
      match?.path?.events?.find((e) => e.kind === "setup")?.bar_time?.trim() ||
      match?.bar_time?.trim() ||
      "";
    if (setup && !times.includes(setup)) times.unshift(setup);
    if (selectedTradeExit) times.push(selectedTradeExit);
    return Array.from(new Set(times.filter(Boolean)));
  }, [match, selectedTradeExit]);

  const [locateArmed, setLocateArmed] = useState(false);
  const [ownLegendHidden, setOwnLegendHidden] = useState(readLegendHidden);
  const legendHidden = legendHiddenProp ?? ownLegendHidden;
  const candleColors = useMemo(() => {
    const s: StrategyRdCandleScheme =
      STRATEGY_RD_CANDLE_SCHEMES.find((x) => x.id === candleScheme) ?? STRATEGY_RD_CANDLE_SCHEMES[0];
    return { up: s.up, down: s.down, wick: s.wick, borderUp: s.borderUp, borderDown: s.borderDown };
  }, [candleScheme]);
  const toggleLegend = () => {
    const next = !legendHidden;
    if (onLegendHiddenChange) {
      onLegendHiddenChange(next);
      return;
    }
    setOwnLegendHidden(next);
    writeLegendHidden(next);
  };
  const [localFrameGuideKey, setLocalFrameGuideKey] = useState<string | null>(null);
  const sawPreviewLoadingRef = useRef(false);

  useEffect(() => {
    if (!locateArmed) {
      sawPreviewLoadingRef.current = false;
      return;
    }
    if (loading) {
      sawPreviewLoadingRef.current = true;
      return;
    }
    if (!sawPreviewLoadingRef.current || !previewCommitted) return;
    setLocateArmed(false);
    // Preview finish → full-window overview (not concept zoom).
    setLocalFrameGuideKey(`preview-overview:${Date.now()}`);
  }, [loading, locateArmed, previewCommitted]);

  const resolvedFrameGuideKey = useMemo(() => {
    // Rail focus wins (concept zoom). Else preview/local overview. Do NOT append
    // selected match — chart clicks must keep the current viewport.
    if (focusMatchToken?.trim()) return focusMatchToken.trim();
    if (localFrameGuideKey?.trim()) return localFrameGuideKey.trim();
    if (previewCommitted && frameGuideKey?.trim()) {
      return `overview:${frameGuideKey.trim()}`;
    }
    return frameGuideKey?.trim() || null;
  }, [focusMatchToken, frameGuideKey, localFrameGuideKey, previewCommitted]);

  const frameFit: "concept" | "full" | "trade" = focusMatchToken?.trim()
    ? selectedTradeExit
      ? "trade"
      : "concept"
    : "full";

  const highlightBarTime =
    strategyRdPathHighlightBarTime(match?.path) || match?.bar_time || null;
  const pathIncomplete = showPathNotice && strategyRdShouldShowIncompleteEntryBanner({
    entryGuidance,
    path: match?.path,
  })
    ? strategyRdIncompleteEntryMessage({
        entryGuidance,
        path: match?.path,
      })
    : null;
  const pathIncompleteFromBeCopy = Boolean(
    entryGuidance?.user_message?.trim() ||
      entryGuidance?.advice?.trim() ||
      match?.path?.user_message?.trim() ||
      match?.path?.advice?.trim()
  );
  const pathZone = useMemo(
    () => (match?.path ? strategyRdPathSetupZone(match.path) : null),
    [match]
  );
  const metaZone = useMemo(
    () => (match ? extractStrategyRdMetaZone(match.meta) : null),
    [match]
  );
  const setupIsLevelPair = strategyRdIsLevelPairSetupId(
    setupId || match?.setup_id || match?.pattern_id
  );
  const setupIdKey = (setupId || match?.setup_id || match?.pattern_id || "")
    .trim()
    .toLowerCase();
  const setupIsLiq =
    setupIdKey === "liq_sweep" ||
    setupIdKey === "liq_run" ||
    (setupIdKey.includes("liq") &&
      (setupIdKey.includes("sweep") || setupIdKey.includes("run")));
  // H/L + liquidity: no "Setup range" legend box (liq uses spanned Pool + Take/Sweep|Run).
  const zoneSource =
    setupIsLevelPair || setupIsLiq
      ? null
      : pathZone ?? (metaZone && metaZone.high > metaZone.low ? metaZone : null);
  const zoneLabel =
    setupIsLevelPair || setupIsLiq
      ? null
      : pathZone?.label ?? (zoneSource && !pathZone && metaZone ? metaZone.label : null);
  const scanRuleTrades =
    mode === "scan" && ruleTrades && ruleTrades.length > 0 ? ruleTrades : null;
  const captionLevels =
    levels?.valid &&
    Number.isFinite(levels.entry) &&
    Number.isFinite(levels.stop) &&
    Number.isFinite(levels.target)
      ? { entry: levels.entry, stop: levels.stop, target: levels.target }
      : null;
  const pathLevels = strategyRdPathAppliedLevels(match?.path);
  const highlightLevels =
    scanRuleTrades != null ? null : pathLevels ?? captionLevels;
  const pathLevelsCaption =
    scanRuleTrades != null || !pathLevels
      ? null
      : strategyRdPathLevelsCaption(match?.path, formatStrategyRdPrice);
  // Stable between renders: a new array each render made the chart redraw every trade
  // on any change anywhere on the page (typing a risk %, opening a panel…).
  const chartTrades = useMemo(() => (scanRuleTrades ? Array.from(scanRuleTrades) : []), [scanRuleTrades]);
  const chartSelectedTradeId = selectedTradeId ?? (match ? strategyRdMatchKey(match) : null);
  const selectableHits = useMemo(
    () => strategyRdMatchChartSelectHits(matches),
    [matches]
  );
  const confluenceIds = useMemo(
    () =>
      (confluences ?? []).map((c) => (typeof c === "string" ? c : c.id).trim()).filter(Boolean),
    [confluences]
  );
  const maLines = useMemo(
    () => [
      ...STRATEGY_RD_MA_SPECS.filter((spec) => enabledLaneBOverlays.includes(spec.id)).map((spec) => ({
        title: spec.title,
        color: spec.color,
      })),
      ...strategyRdSrLegendItems(
        srScopes,
        confluenceIds,
        srRails,
        parseStrategyRdMatchSr(match?.meta)
      ),
    ],
    [confluenceIds, enabledLaneBOverlays, match, srRails, srScopes]
  );
  const showPathLegend =
    pathHighlights.some((h) => h.showText) || Boolean(pathZone);
  const pathLegendKinds = useMemo(() => {
    const kinds = new Set<
      | "break"
      | "rejection"
      | "retest"
      | "location_armed"
      | "respect"
      | "reject"
      | "break_retest"
      | "entry"
      | "setup"
      | "invalid"
      | "marker"
    >();
    for (const h of pathHighlights) {
      if (h.kind) kinds.add(h.kind);
    }
    return kinds;
  }, [pathHighlights]);
  const conceptLegendLabels = useMemo(() => {
    const labels: string[] = [];
    const seen = new Set<string>();
    const suffix = match?.bar_time?.trim() || "";
    for (const d of conceptDrawings) {
      if (!d.id.startsWith("setup-zone:")) continue;
      if (suffix && !d.id.endsWith(`:${suffix}`) && d.id.split(":").length > 2) continue;
      const label = (d.label || "").trim().replace(/\s*\n\s*/g, " ");
      if (!label || seen.has(label)) continue;
      seen.add(label);
      labels.push(label);
    }
    return labels.slice(0, 3);
  }, [conceptDrawings, match?.bar_time]);

  // Fill mode: the chart takes the box's measured height.
  const chartBoxRef = useRef<HTMLDivElement | null>(null);
  const [fillHeight, setFillHeight] = useState(480);
  useEffect(() => {
    if (!fill) return;
    const el = chartBoxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) setFillHeight((prev) => (Math.abs(prev - h) > 2 ? h : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill]);

  const hasWarmCandles = normalized.length > 0;
  const matchCount = matches.length;
  const liveCaption =
    matchCount === 0
      ? "No matches for this combo in this window"
      : matchCount === 1
        ? "1 match in this window"
        : `${matchCount} matches in this window`;
  const title = mode === "scan" ? "Live preview (locked)" : "Live preview";
  const summaryParts = identityReady
    ? [
        symbolLabel || match?.symbol,
        loading
          ? loadingLabel || (mode === "scan" ? "Scanning…" : "Loading preview…")
          : mode === "preview" && !previewCommitted
            ? "Does this recipe fire?"
            : liveCaption,
        detectCountLabel && (mode === "scan" || previewCommitted) ? detectCountLabel : null,
      ].filter(Boolean)
    : ["Confirm symbol to load preview"];
  const levelsCaption = !identityReady
    ? null
    : captionLevels
    ? pathLevelsCaption ||
      `Entry ${formatStrategyRdPrice(captionLevels.entry)} · SL ${formatStrategyRdPrice(captionLevels.stop)} · TP ${formatStrategyRdPrice(captionLevels.target)}`
    : pathLevelsCaption;
  const matchParts =
    identityReady && match
      ? [
          directionLabel(displayDirection),
          candleType ? formatExecutionCandleTypeLabel(match.candle_type) || match.candle_type : null,
          formatTradeDateTimeForDisplay(match.bar_time, timeZone),
          resolvedSessionLabel,
        ].filter(Boolean)
      : [];
  const captionParts = [...summaryParts, ...matchParts, levelsCaption].filter(Boolean);
  // Fill mode's bottom bar: the symbol is on the chart already.
  const statusSummary = (identityReady ? summaryParts.slice(1) : summaryParts).join(" · ");
  const statusLine = [statusSummary, ...matchParts, levelsCaption].filter(Boolean).join(" · ");

  const emptyMessage = !identityReady
    ? "Confirm symbol to load preview"
    : loading
      ? loadingLabel || "Loading candles…"
      : mode === "preview"
        ? "Set your recipe, then click Preview"
        : "Scan to lock matches for backtest";

  return (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col",
        fill
          ? "h-full gap-0 overflow-hidden bg-background"
          : "gap-2 rounded-[6px] border border-border bg-background p-3"
      )}
      data-strategy-rd-detect-chart=""
      data-strategy-rd-detect-mode={mode}
    >
      {fill ? null : (
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          {fill ? null : <h3 className={strategyRdSectionTitleClassName}>{title}</h3>}
          {fill ? (
            <>
              <p className={cn(strategyRdMetaClassName, "truncate text-xs")}>
                {summaryParts.join(" · ")}
                {timeZoneLabel ? ` · Times in ${timeZoneLabel}` : ""}
              </p>
              {matchParts.length > 0 || levelsCaption ? (
                <p
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-white"
                  data-strategy-rd-selected-match=""
                >
                  {matchParts.length > 0 ? (
                    <span className="font-medium">
                      <span className="text-[color:var(--brand-tropical-surf)]">Selected</span>{" "}
                      {matchParts.join(" · ")}
                    </span>
                  ) : null}
                  {levelsCaption ? (
                    <span className="tabular-nums text-muted-foreground">{levelsCaption}</span>
                  ) : null}
                </p>
              ) : null}
            </>
          ) : (
            <p className={strategyRdMetaClassName}>{captionParts.join(" · ")}</p>
          )}
        </div>
        {captionActions}
        {mode === "preview" && onPreview ? (
          <div className="flex shrink-0 items-center gap-2">
            {matchesRailHidden && onShowMatches ? (
              <Button
                type="button"
                variant="outline-brand"
                size="sm"
                data-font="primary"
                aria-label="Show matches"
                onClick={onShowMatches}
                className={cn(
                  strategyRdBrandActionButtonClassName,
                  "rounded-full normal-case tracking-normal"
                )}
              >
                <span>Matches</span>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              data-strategy-rd-preview=""
              onClick={() => {
                setLocateArmed(true);
                // Snap to full-window immediately (warm candles) and again when loading ends.
                setLocalFrameGuideKey(`preview-overview:${Date.now()}`);
                onPreview();
              }}
              disabled={previewDisabled || loading}
              className="shrink-0"
            >
              {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span className="normal-case tracking-normal">
                {loading ? "Previewing" : "Preview"}
              </span>
            </Button>
          </div>
        ) : matchesRailHidden && onShowMatches ? (
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            aria-label="Show matches"
            onClick={onShowMatches}
            className={cn(
              strategyRdBrandActionButtonClassName,
              "shrink-0 rounded-full normal-case tracking-normal"
            )}
          >
            <span>Matches</span>
          </Button>
        ) : null}
      </div>
      )}

      {pathIncomplete || (diagnostics && diagnostics.length > 0) || previewEmpty ? (
        <div className={cn(fill && "m-2 mb-0", "flex flex-col gap-2 rounded-[6px] border border-[color:var(--brand-tropical-surf)]/40 bg-[color-mix(in_oklab,var(--brand-tropical-surf)_8%,transparent)] px-3 py-2.5")}>
          {previewEmpty ? (
            <p className="font-[family-name:var(--font-primary)] text-sm text-[color:var(--brand-tropical-surf)]">
              {previewEmpty.title}
            </p>
          ) : null}
          {pathIncomplete ? (
            <p className="font-[family-name:var(--font-primary)] text-sm leading-snug text-gray-500 dark:text-white">
              {pathIncompleteFromBeCopy
                ? pathIncomplete
                : `${pathIncomplete}. Setup still shows; there is no entry on this path.`}
            </p>
          ) : null}
          {previewEmpty?.body_markdown ? (
            <div className="max-w-none font-[family-name:var(--font-primary)] text-sm leading-snug text-muted-foreground [&_p]:my-1 [&_strong]:font-[family-name:var(--font-primary)] [&_strong]:font-medium [&_strong]:text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {previewEmpty.body_markdown}
              </ReactMarkdown>
            </div>
          ) : null}
          {diagnostics && diagnostics.length > 0 ? (
            <p className="font-[family-name:var(--font-primary)] text-xs leading-snug text-muted-foreground">
              {diagnostics.join(" · ")}
            </p>
          ) : null}
          {previewEmpty?.suggested_actions?.length ? (
            <div className="flex flex-wrap gap-2 pt-0.5">
              {previewEmpty.suggested_actions.map((action) => (
                <Button
                  key={action.id}
                  type="button"
                  variant="outline-brand"
                  size="sm"
                  data-font="primary"
                  className={strategyRdBrandActionButtonClassName}
                  onClick={() => onPreviewEmptyAction?.(action)}
                >
                  <span className="normal-case tracking-normal">{action.label}</span>
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        ref={chartBoxRef}
        className={cn(
          "relative w-full overflow-hidden bg-background",
          fill ? "min-h-[240px] flex-1" : "min-h-[620px] rounded-[6px] border border-sidebar-border p-1"
        )}
      >
        {hasWarmCandles ? (
          <ExecutionCandlesChart
            candles={normalized}
            trades={chartTrades}
            selectedTradeId={chartSelectedTradeId}
            onSelectTrade={onSelectTrade}
            onAskAbout={onAskAbout}
            selectableHits={selectableHits}
            candleType={candleType || match?.candle_type || "15m"}
            lockTimeframe={!onCandleTypeChange}
            lockInstrument={!onInstrumentChange}
            allowedCandleTypes={allowedCandleTypes}
            onCandleTypeChange={onCandleTypeChange}
            onInstrumentChange={onInstrumentChange}
            symbolProvider={symbolProvider}
            instrumentLabel={symbolLabel || match?.symbol || "Preview"}
            highlightBarTime={pathHighlights.length > 0 ? null : highlightBarTime}
            highlightDirection={displayDirection}
            highlightLabel={
              match
                ? strategyRdConceptChartLabel(match.setup_id || match.pattern_id)
                : "Setup"
            }
            pathHighlights={pathHighlights.length > 0 ? pathHighlights : null}
            highlightLevels={highlightLevels}
            conceptDrawings={conceptDrawings}
            enabledLaneBOverlays={enabledLaneBOverlays}
            laneBOscParams={laneBOscParams}
            strategyRdIndicatorSeries={chipTaggedSeries}
            strategyRdChips={chartChips}
            frameAroundBarTimes={frameAroundBarTimes}
            frameGuideKey={resolvedFrameGuideKey}
            frameFit={frameFit}
            height={fill ? Math.max(232, fillHeight) : STRATEGY_RD_CHART_HEIGHT}
            displayTimeZone={timeZone}
            denseChrome
            showNavigationControls
            showPnlBackgroundOverlay={false}
            chartWorkspace="strategyRd"
            candleColors={candleColors}
            mountVolumeHistogram={mountVolumeHistogram}
          />
        ) : (
          <div className={cn("flex items-center justify-center px-4", fill ? "h-full" : "min-h-[620px]")}>
            <p className={strategyRdMetaClassName}>{emptyMessage}</p>
          </div>
        )}
      </div>

      {hasWarmCandles && (showPathLegend || highlightLevels || scanRuleTrades || maLines.length > 0) ? (
        // The key is always on in the Lab: no show / hide.
        <StrategyRdChartLegend
            direction={displayDirection}
            showLevels={Boolean(highlightLevels || scanRuleTrades)}
            showPath={showPathLegend}
            pathKinds={pathLegendKinds}
            conceptLabels={conceptLegendLabels}
            zoneLabel={zoneLabel}
            maLines={maLines}
            slim={fill}
          />
      ) : null}

      {fill ? (
        <div
          className="flex h-12 shrink-0 items-center gap-3 !border-0 !border-solid !border-border !border-t px-3"
          data-strategy-rd-chart-bar=""
        >
          {footerStart}
          {footerStart ? <span className="h-5 w-px shrink-0 bg-border" aria-hidden /> : null}
          {/* What the chart shows: how many setups, then the selected one (side, time, levels). */}
          <div
            className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden font-[family-name:var(--font-primary)] text-xs text-muted-foreground"
            title={statusLine}
            data-strategy-rd-selected-match=""
          >
            {/* While loading the chart shows its own state; the bar just waits. */}
            {loading ? null : (
              <>
                {statusSummary ? (
                  <span className="shrink-0 whitespace-nowrap font-medium text-[color:var(--brand-tropical-surf)]">{statusSummary}</span>
                ) : null}
                {identityReady && match ? (
                  <>
                    <span className="h-4 w-px shrink-0 bg-border" aria-hidden />
                    {displayDirection === "long" || displayDirection === "short" ? (
                      <TradePositionSideBadge side={displayDirection} className="h-5 shrink-0 px-1.5 text-[11px]" />
                    ) : null}
                    <span className="shrink-0 whitespace-nowrap text-gray-500 dark:text-white">
                      {formatTradeDateTimeForDisplay(match.bar_time, timeZone)}
                    </span>
                    {resolvedSessionLabel ? (
                      <span className="shrink-0 whitespace-nowrap">{resolvedSessionLabel}</span>
                    ) : null}
                    {levelsCaption ? <span className="min-w-0 truncate tabular-nums">{levelsCaption}</span> : null}
                  </>
                ) : levelsCaption ? (
                  <span className="min-w-0 truncate tabular-nums">{levelsCaption}</span>
                ) : null}
              </>
            )}
          </div>
          {captionActions}
          {mode === "preview" && onPreview ? (
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              data-strategy-rd-preview=""
              onClick={() => {
                setLocateArmed(true);
                setLocalFrameGuideKey(`preview-overview:${Date.now()}`);
                onPreview();
              }}
              disabled={previewDisabled || loading}
              className="h-7 shrink-0 px-3"
            >
              {loading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
              <span className="normal-case tracking-normal">{loading ? "Previewing" : "Preview"}</span>
            </Button>
          ) : null}
          {footerEnd ? <span className="h-4 w-px shrink-0 bg-border" aria-hidden /> : null}
          {footerEnd}
        </div>
      ) : (
        <p className={strategyRdMetaClassName}>
          {mode === "scan"
            ? "Same detect as Preview, locked for backtest. Run backtest uses this match list. Not a different detector."
            : "Faint washes mark other hits. Click one to focus: labels + geometry, no dark wash."}
        </p>
      )}
    </div>
  );
}
